import React from "react";
import axios from "axios";
import { TrendingUp, Package, ArrowDown, RotateCcw, Wallet, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDkk = (n) => `${Number(n || 0).toLocaleString("da-DK", { maximumFractionDigits: 0 })} kr`;

export default function PickupAnalyticsTab({ token }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`${API}/admin/pickup-points/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="text-brand-text2 text-center py-10" data-testid="pp-analytics-loading">Indlæser analytics...</div>;
  if (!data) return <div className="text-brand-text2 text-center py-10">Kunne ikke hente data</div>;

  const t = data.totals || {};
  return (
    <div className="space-y-5" data-testid="pp-analytics">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-brand p-5">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold flex items-center gap-1">
            <Building2 size={11} /> Aktive partnere
          </div>
          <div className="font-display font-bold text-3xl text-brand-dark mt-2" data-testid="pp-active-partners">{t.active_partners}</div>
        </div>
        <div className="bg-white border border-brand p-5">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold flex items-center gap-1">
            <Package size={11} /> Modtaget i md
          </div>
          <div className="font-display font-bold text-3xl text-brand-dark mt-2">{t.received_mtd}</div>
        </div>
        <div className="bg-white border border-brand p-5">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold flex items-center gap-1">
            <ArrowDown size={11} /> Udleveret i md
          </div>
          <div className="font-display font-bold text-3xl text-brand-dark mt-2">{t.handed_over_mtd}</div>
        </div>
        <div className="bg-white border border-brand p-5">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold flex items-center gap-1">
            <RotateCcw size={11} /> Returer i md
          </div>
          <div className="font-display font-bold text-3xl text-brand-dark mt-2">{t.returns_mtd}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-amber-50 border border-amber-200 p-5">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">I butik nu</div>
          <div className="font-display font-bold text-3xl text-amber-900 mt-2">{t.in_shop_now}</div>
          <div className="text-xs text-amber-700 mt-1">venter afhentning</div>
        </div>
        <div className="bg-red-50 border border-red-200 p-5">
          <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Retur-kø</div>
          <div className="font-display font-bold text-3xl text-red-900 mt-2">{t.return_queue_now}</div>
          <div className="text-xs text-red-700 mt-1">venter på chauffør</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 p-5">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Udbetalt i md</div>
          <div className="font-display font-bold text-3xl text-emerald-900 mt-2">{fmtDkk(t.paid_out_mtd_dkk)}</div>
          <div className="text-xs text-emerald-700 mt-1">total til partnere</div>
        </div>
      </div>

      <div className="bg-white border border-brand">
        <div className="p-5 border-b border-brand">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-brand-red" />
            <div className="font-display font-bold text-brand-dark">Top shops (sidste 30 dage)</div>
          </div>
        </div>
        <table className="w-full text-sm" data-testid="pp-top-shops">
          <thead className="bg-brand-bg text-brand-text2 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Shop</th>
              <th className="text-right p-3">Modtaget</th>
              <th className="text-right p-3">Udleveret</th>
              <th className="text-right p-3">Retur</th>
              <th className="text-right p-3">Retention</th>
              <th className="text-right p-3">Kommission</th>
            </tr>
          </thead>
          <tbody>
            {data.top_shops?.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-brand-text2">Ingen data endnu</td></tr>
            )}
            {data.top_shops?.map((s) => (
              <tr key={s.partner_id} className="border-t border-brand" data-testid={`pp-row-${s.partner_id}`}>
                <td className="p-3">
                  <div className="font-semibold text-brand-dark">{s.shop_name}</div>
                  <div className="text-xs text-brand-text2">{s.shop_city}</div>
                </td>
                <td className="p-3 text-right">{s.received_30d}</td>
                <td className="p-3 text-right font-semibold">{s.handed_over_30d}</td>
                <td className="p-3 text-right">{s.returned_30d}</td>
                <td className="p-3 text-right">{(s.retention_rate * 100).toFixed(0)}%</td>
                <td className="p-3 text-right font-semibold text-brand-red">{fmtDkk(s.est_commission_30d_dkk)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
